import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { fmtDate, relTime } from "@/lib/orgUtils";
import { Download, RefreshCw, Link2, CheckCircle2, XCircle, AlertTriangle, Copy } from "lucide-react";

export function OrganizationTab() {
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  useEffect(() => { api.get("/org").then((r) => { setOrg(r.data); setName(r.data.name); }); }, []);
  if (!org) return null;
  return (
    <div className="max-w-xl space-y-5">
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10">
        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Organization name</label>
        <div className="mt-1.5 flex gap-2">
          <input data-testid="org-name-input" value={name} onChange={(e) => setName(e.target.value)}
            className="flex-1 h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
          <button data-testid="save-org-btn" onClick={() => api.put("/org", { name }).then(() => toast.success("Saved"))}
            className="px-5 h-11 rounded-xl bg-[#6161FF] text-white font-bold">Save</button>
        </div>
        <label className="mt-4 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Workspace slug (read-only)</label>
        <p className="mt-1 font-mono font-bold text-[#4b4e63]" data-testid="org-slug">{org.slug}</p>
      </div>
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10" data-testid="plan-card">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Subscription plan</p>
        <div className="mt-3 flex items-center gap-4">
          <span className="px-3 py-1 rounded-full bg-[#6161FF]/10 text-[#6161FF] text-sm font-black capitalize">{org.plan?.billing_cycle}</span>
          <span className={`px-3 py-1 rounded-full text-sm font-black capitalize ${org.plan?.status === "active" ? "bg-[#00C875]/15 text-[#00925a]" : "bg-[#E2445C]/12 text-[#E2445C]"}`}>{org.plan?.status}</span>
          <span className="text-sm font-semibold text-[#676a82]">Renews {org.plan?.renewal_date}</span>
        </div>
        <p className="mt-3 text-xs text-[#9699AD] font-medium">Plan changes are handled by Zefficacy platform operations.</p>
      </div>
    </div>
  );
}

export function DataTab() {
  const [sources, setSources] = useState({ sources: [], gsheet_api_configured: false });
  const [snaps, setSnaps] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/import/sources").then((r) => setSources(r.data)).catch(() => {});
    api.get("/import/jobs").then((r) => setSnaps(r.data.slice(0, 3))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = (path, fname) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("organa_token")}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = fname;
        a.click();
      });
  };

  const gsheet = sources.sources.find((s) => s.type === "gsheet");
  return (
    <div className="max-w-2xl space-y-5">
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10" data-testid="gsheet-connection-card">
        <div className="flex items-center gap-2">
          <Link2 size={16} className="text-[#579BFC]" />
          <h3 className="font-black">Google Sheets connection</h3>
          {sources.gsheet_api_configured ? (
            <span className="ml-auto flex items-center gap-1 text-xs font-bold text-[#00925a]"><CheckCircle2 size={13} /> API configured</span>
          ) : (
            <span className="ml-auto flex items-center gap-1 text-xs font-bold text-[#c97a10]"><AlertTriangle size={13} /> Not configured</span>
          )}
        </div>
        <p className="mt-2 text-sm text-[#676a82] font-medium">
          {sources.gsheet_api_configured
            ? "Reading sheets via the Sheets API v4."
            : "Set GOOGLE_SHEETS_API_KEY in the environment to enable API access. Until then, link-shared sheets are read via the public CSV export fallback."}
        </p>
        {gsheet && (
          <div className="mt-3 p-3 rounded-xl bg-[#F6F7FB] text-sm">
            <p className="font-bold truncate">{gsheet.gsheet_url}</p>
            <p className="text-xs font-semibold text-[#9699AD] mt-0.5">Last synced {gsheet.last_synced_at ? relTime(gsheet.last_synced_at) : "never"}</p>
            <button data-testid="data-refresh-gsheet" disabled={busy}
              onClick={() => { setBusy(true); api.post("/import/gsheet/refresh").then((r) => toast.success(`Synced ${r.data.counts.ok} rows`)).catch((e) => toast.error(errMsg(e))).finally(() => { setBusy(false); load(); }); }}
              className="mt-2 flex items-center gap-1.5 px-3 h-8 rounded-lg bg-[#579BFC] text-white text-xs font-bold disabled:opacity-50">
              <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh now
            </button>
          </div>
        )}
      </div>
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10">
        <h3 className="font-black">Templates</h3>
        <div className="mt-3 flex gap-2">
          <button data-testid="data-template-csv" onClick={() => download("/import/template.csv", "organa_template.csv")} className="flex items-center gap-1.5 px-3 h-9 rounded-xl border border-[#181B34]/10 text-xs font-bold"><Download size={13} /> CSV template</button>
          <button data-testid="data-template-xlsx" onClick={() => download("/import/template.xlsx", "organa_template.xlsx")} className="flex items-center gap-1.5 px-3 h-9 rounded-xl border border-[#181B34]/10 text-xs font-bold"><Download size={13} /> XLSX template</button>
        </div>
      </div>
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10">
        <h3 className="font-black">Recent snapshots (rollback points)</h3>
        <p className="text-sm text-[#676a82] font-medium mt-1">The last 3 directory states are kept. Roll back from the Imports page.</p>
        <div className="mt-3 space-y-1.5">
          {snaps.map((j) => (
            <p key={j.id} className="text-sm font-semibold text-[#4b4e63]">{fmtDate(j.timestamp)} · {j.source_type.toUpperCase()} import by {j.actor}</p>
          ))}
          {snaps.length === 0 && <p className="text-sm text-[#9699AD] font-medium">No snapshots yet.</p>}
        </div>
      </div>
    </div>
  );
}

export function NotificationsTab() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  if (!settings) return null;
  const n = settings.notifications || {};
  const save = (patch) => {
    const next = { ...n, ...patch };
    setSettings({ ...settings, notifications: next });
    api.put("/settings", { notifications: next }).then(() => toast.success("Saved")).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <div className="max-w-xl space-y-2">
      <p className="text-sm text-[#676a82] font-medium mb-4">Admin email notifications (sent via Resend when configured).</p>
      {[["import_errors", "Import completed with errors"], ["sheet_failure", "Google Sheet refresh failure"], ["rollback", "Rollback performed"], ["weekly_digest", "Weekly digest (imports, moves, profile completions)"]].map(([k, l]) => (
        <div key={k} className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-[#181B34]/10">
          <span className="text-sm font-bold">{l}</span>
          <Switch data-testid={`notif-${k}`} checked={n[k] !== false && (k !== "weekly_digest" || n[k] === true)} onCheckedChange={(v) => save({ [k]: v })} />
        </div>
      ))}
    </div>
  );
}

export function MaintenanceTab() {
  const [report, setReport] = useState(null);
  useEffect(() => { api.get("/maintenance/report").then((r) => setReport(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!report) return null;
  const exportDir = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/export-directory`, { headers: { Authorization: `Bearer ${localStorage.getItem("organa_token")}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "directory_export.csv";
        a.click();
      });
  };
  return (
    <div className="max-w-2xl space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-white border border-[#181B34]/10" style={{ borderTop: "4px solid #00C875" }}>
          <p className="text-3xl font-black">{report.total_active}</p>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mt-1">Active employees</p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-[#181B34]/10" style={{ borderTop: "4px solid #9699AD" }}>
          <p className="text-3xl font-black">{report.total_exited}</p>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mt-1">Exited</p>
        </div>
      </div>
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10" data-testid="duplicates-report">
        <h3 className="font-black flex items-center gap-2">Duplicate detector {report.duplicates.length > 0 ? <XCircle size={15} className="text-[#E2445C]" /> : <CheckCircle2 size={15} className="text-[#00C875]" />}</h3>
        {report.duplicates.length === 0 ? <p className="text-sm text-[#676a82] font-medium mt-1">No duplicates found (by name+DOJ or email).</p> :
          report.duplicates.map((d, i) => <p key={i} className="text-sm font-semibold mt-1 text-[#E2445C]">{d.a} ↔ {d.b} <span className="text-xs text-[#9699AD]">({d.reason})</span></p>)}
      </div>
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10" data-testid="orphans-report">
        <h3 className="font-black flex items-center gap-2">Orphaned nodes {report.orphans.length > 0 ? <AlertTriangle size={15} className="text-[#c97a10]" /> : <CheckCircle2 size={15} className="text-[#00C875]" />}</h3>
        {report.orphans.length === 0 ? <p className="text-sm text-[#676a82] font-medium mt-1">Every employee's manager is present and active.</p> :
          report.orphans.map((o, i) => <p key={i} className="text-sm font-semibold mt-1">{o.employee_id} — {o.full_name} <span className="text-xs text-[#9699AD]">reports to missing/exited {o.manager}</span></p>)}
      </div>
      <button data-testid="export-directory-btn" onClick={exportDir} className="flex items-center gap-2 px-5 h-11 rounded-xl bg-[#181B34] text-white font-bold text-sm">
        <Download size={15} /> Export directory (CSV backup)
      </button>
    </div>
  );
}

export function PreferencesTab() {
  const [prefs, setPrefs] = useState(null);
  const [settings, setSettings] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    api.get("/preferences").then((r) => setPrefs(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);
  if (!prefs || !settings) return null;
  const savePref = (patch) => {
    setPrefs({ ...prefs, ...patch });
    api.put("/preferences", patch).then(() => toast.success("Preference saved"));
  };
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  return (
    <div className="max-w-xl space-y-5">
      <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10">
        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Default layout (yours)</label>
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {[["classic", "Classic"], ["horizontal", "Horizontal"], ["outline", "Outline"], ["radial", "Radial"], ["team_grid", "Team Grid"]].map(([v, l]) => (
            <button key={v} data-testid={`pref-layout-${v}`} onClick={() => savePref({ default_layout: v })}
              className={`px-3 h-9 rounded-full text-xs font-bold ${prefs.default_layout === v ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>{l}</button>
          ))}
        </div>
        <label className="mt-5 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Density</label>
        <div className="mt-2 flex gap-1.5">
          {["comfortable", "compact"].map((d) => (
            <button key={d} data-testid={`pref-density-${d}`} onClick={() => savePref({ density: d })}
              className={`px-3 h-9 rounded-full text-xs font-bold capitalize ${prefs.density === d ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>{d}</button>
          ))}
        </div>
      </div>
      {isAdmin && (
        <div className="p-5 rounded-2xl bg-white border border-[#181B34]/10">
          <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Tenant date format</label>
          <div className="mt-2 flex gap-1.5">
            {["DD-MM-YYYY", "YYYY-MM-DD", "MM/DD/YYYY"].map((f) => (
              <button key={f} data-testid={`pref-dateformat-${f}`}
                onClick={() => { setSettings({ ...settings, date_format: f }); api.put("/settings", { date_format: f }).then(() => toast.success("Saved")); }}
                className={`px-3 h-9 rounded-full text-xs font-bold ${settings.date_format === f ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>{f}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
